import React, { useRef, useEffect, useState, useMemo } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import FusionChip from '@directv-digital/fusion.atoms.chip';
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import Button from '@directv-digital/fusion.atoms.button';
import { UpIcon, DownIcon } from '@directv-digital/fusion.atoms.icons';
import { getChoiceKey, getChoiceLabel, SearchChoiceLike } from "./utils/choice-key";
import { useLocationHref } from "src/hooks/useLocationHref";

export interface FilterChipsProps {
    selectedChannels: SearchChoiceLike[];
    theming: 'light' | 'dark';
    // [WAT-11775] The WHOLE choice, not `{ displayText }`. The old signature promised the
    // callback needed only a name, but removal is keyed on the full identity — so a
    // consumer that conformed to the declared type
    // (`handleRemoveTag={({displayText}) => onSelectTag({displayText})}`) compiled clean,
    // keyed to a different bucket, failed the membership test, and clicking a chip's X GREW
    // the selection instead of removing one.
    handleRemoveTag: (tag: SearchChoiceLike) => void;
}

/** [WAT-11775] A chip's delete label: name, then the channel number where there is one, then
 *  a position among identically-announced chips where even that is not enough. */
const buildRemoveLabel = (label: string, chnlNum: any, twin: { ordinal: number; total: number } | null): string => {
  const base = chnlNum ? `remove ${label?.toLowerCase()} channel ${chnlNum}` : `remove ${label?.toLowerCase()}`;
  return twin ? `${base}, ${twin.ordinal} of ${twin.total}` : base;
};

{ /* Generated by Copilot- Start */ }
const FilterChips = (props: FilterChipsProps) => {
  const locationHrefUrl = useLocationHref();
  const { selectedChannels, theming, handleRemoveTag } = props;
  const contentRef = useRef(null);
  const [showAllTags, setShowAllTags] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [screenType, setScreenType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  useEffect(() => {
    const handleResize = () => {
      if (window?.innerWidth <= 600) {
        setScreenType('mobile');
        // WAT-11342: read the boundary from the theme instead of writing it
        // out. `values.md` is the exact number the CSS `md:` sx keys resolve
        // from, so this check and the styles can never disagree — it was 1199
        // and is now 1200, and this line is correct either way.
      } else if (window?.innerWidth >= DtvTheme.breakpoints.values.md) {
        setScreenType('desktop');
      } else {
        setScreenType('tablet');
      }
    };
    handleResize();
    window?.addEventListener?.("resize", handleResize);
    return () => window?.removeEventListener?.("resize", handleResize);
  }, []);
  // [WAT-11775] One key per selected chip, computed once per selection change.
  const chipKeys = useMemo(
    () => (selectedChannels || []).map((ch: SearchChoiceLike) => getChoiceKey(ch)),
    [selectedChannels]
  );
  // [WAT-11775] Position among identically-announced chips. The channel number usually
  // separates two same-named chips — but the live feed carries TWO rows both named
  // "Sports Mix" both on 205, and there the number does not, so both delete controls would
  // read "remove sports mix channel 205" and getByLabelText would throw on the ambiguity.
  const chipTwinInfo = useMemo(() => {
    const list = selectedChannels || [];
    const keys = list.map((ch: SearchChoiceLike) => `${getChoiceLabel(ch)?.toLowerCase()}|${ch?.chnlNum ?? ""}`);
    return keys.map((key, ix) => {
      const twins = keys.filter(k => k === key);
      return twins.length > 1 ? {
        ordinal: keys.slice(0, ix + 1).filter(k => k === key).length,
        total: twins.length
      } : null;
    });
  }, [selectedChannels]);
  useEffect(() => {
    const contentDiv = contentRef.current;
    if (contentDiv) {
      // Check if the content height exceeds the visible height
      if ((screenType === 'desktop' && contentDiv.scrollHeight > 160) || ((screenType === 'mobile' || screenType === 'tablet') && contentDiv.scrollHeight > 240)) {
        setShowButton(true);
        setShowAllTags(false);
      } else {
        setShowButton(false);
      }
    }
  }, [selectedChannels, screenType]); // Empty dependency array ensures this runs once after initial render
  return (
        <>
            <DtvBox
            ref={contentRef}
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                width: "100%",
                margin: '20px auto 0px',
                maxHeight: 'calc(3 * 48px)', // 3 rows for desktop
                [DtvTheme.breakpoints.between(DtvTheme.breakpoints.values.sm, DtvTheme.breakpoints.values.md)]: {
                maxHeight: 'calc(5 * 48px)' // 3 rows for desktop
                },
                [DtvTheme.breakpoints.down(DtvTheme.breakpoints.values.sm)]: {
                maxHeight: 'calc(5 * 48px)' // 3 rows for desktop
                },
                ...(showAllTags && {
                maxHeight: 'none !important'
                }),
                overflow: 'hidden'
            }}>
                {selectedChannels.map((tag: SearchChoiceLike, tagIx: number) => {
                    // [WAT-11767/11775] Two selected channels can share a display name; key
                    // and exclude on the shared identity so each chip is distinct. Keys are
                    // precomputed once above rather than rebuilt per chip per sibling, which
                    // was ~2n^2 template strings per render on a large selection.
                    const tagKey = chipKeys[tagIx];
                    const label = getChoiceLabel(tag);
                    // The sibling list that goes to analytics. Two fixes over WAT-11767:
                    //  • `filter(Boolean)` — its twin in DropdownList gained this in the same
                    //    PR and this one did not, so a nameless selected channel emitted the
                    //    literal string "search - undefined" to the feed.
                    //  • deduped — allowing two same-named channels to be selected made
                    //    "search - espn, espn" reachable, a value the feed could never
                    //    previously receive.
                    const textLabel = `search - ${[...new Set(selectedChannels
                                .filter((_ch, ix) => chipKeys[ix] !== tagKey)
                                .map(ch => getChoiceLabel(ch)?.toLowerCase())
                                .filter(Boolean))]
                                .join(', ')}`
                    return (<FusionChip
                        color="primary"
                        key={tagKey}
                        labelText={label}
                        mode={theming}
                        tabIndex={-1}
                        variant="outlined"
                        deleteIconProps={{
                            // [WAT-11775] The number is announced where there is one. Two
                            // same-named chips otherwise share the delete label "remove espn",
                            // so a screen-reader user cannot tell which one they are removing
                            // — and `getByLabelText('remove espn')` throws "found multiple
                            // elements", a state impossible before WAT-11767.
                            "aria-label": buildRemoveLabel(label, tag?.chnlNum, chipTwinInfo[tagIx]),
                            "data-linklabel": textLabel,
                            "data-linkposition": "panel_channel lineup",
                            "data-linkdestination": locationHrefUrl
                        }}
                        sx={{
                        "&:hover": {
                        backgroundColor: "transparent !important"
                        },
                        "&.active-chip": {
                        backgroundColor: "transparent !important"
                        },
                        "& .MuiIconButton-root:hover ": {
                        backgroundColor: theming === "dark" ? "#8cd3f514 !important" : "#10264114 !important"
                        },
                        "& .MuiIconButton-root:focus-visible": {
                            outline: `4px solid transparent !important`,
                            outlineColor: `${theming === 'dark' ? '#1fbcf8' : '#3885e1'} !important`,
                            outlineOffset: '0px',
                            borderRadius: "50%"
                        }
                        }}
                        onClick={() => handleRemoveTag(tag)}
                        onDelete={() => handleRemoveTag(tag)}
                        onKeyUp={event => event.key === "Enter" && handleRemoveTag(tag)}
                    />)
                })}
            </DtvBox>
            {showButton && (
                <DtvBox sx={{
 width: "100%",
textAlign: 'center',
margin: '10px auto 0px',
display: 'flex',
alignItems: 'center'
}}>
                    <DtvBox sx={{
 flex: 1,
height: '1px',
backgroundColor: '#E0E0E0',
marginRight: '10px'
}} />
                    <Button
                    buttontype="primary"
                    mode={theming}
                    size="small"
                    rightIcon={showAllTags ? <UpIcon
                        fill={theming === 'dark' ? '#FFFFFF' : '#000000'}
                        /> : <DownIcon
                        fill={theming === 'dark' ? '#FFFFFF' : '#000000'}
                        />}
                    onClick={() => setShowAllTags(!showAllTags)}
                    >
                    {showAllTags ? 'Show Less' : 'Show More'}
                    </Button>
                    <DtvBox sx={{
 flex: 1,
height: '1px',
backgroundColor: '#E0E0E0',
marginLeft: '10px'
}} />
                </DtvBox>
            )}
        </>
  );
};
{ /* Generated by Copilot- End */ }

export const MemoizedFilterChips = React.memo(FilterChips);
